import { config } from '../config';
import { UserInfo } from '../types';
import { beginLogin, completeLogin } from './oauth-pkce';
import { isReviewerFromIdTokenPayload } from './claims';

const TOKEN_KEY = 'hallway_tokens';

/**
 * The OAuth redirect URI for the origin this page is served from.
 *
 * Why: the SPA is reachable on the custom domain and on its CloudFront host, both registered
 * as Cognito callback URLs. The login's single-use state and PKCE verifier live in this
 * origin's sessionStorage, so the callback must return to the same origin — a build-time URI
 * naming the other host made every custom-domain login fail its state check (2026-10-08).
 * The token exchange must send the identical URI, so both use this function.
 *
 * @returns `<current origin>/callback`.
 */
function redirectUri(): string {
  return `${window.location.origin}/callback`;
}

interface TokenSet {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
}

/**
 * Start sign-in: redirect to the Cognito hosted UI with `state` and a PKCE challenge.
 *
 * Why: the callback must only accept a code this tab asked for (011 finding 4).
 * How: the single-use state and verifier live in sessionStorage, scoped to this tab.
 *
 * @returns Resolves once navigation has been requested.
 */
export async function startLogin(): Promise<void> {
  window.location.assign(await beginLogin(window.sessionStorage, {
    authorizeEndpoint: `https://${config.cognitoDomain}.auth.us-east-1.amazoncognito.com/oauth2/authorize`,
    clientId: config.cognitoClientId,
    redirectUri: redirectUri(),
  }));
}

/** Build Cognito hosted UI logout URL (does NOT clear tokens — call logout() for that) */
export function getLogoutUrl(): string {
  const params = new URLSearchParams({
    client_id: config.cognitoClientId,
    // Return to the origin the user signed out from (registered as a logout URL, like the callback).
    logout_uri: `${window.location.origin}/`,
  });
  return `https://${config.cognitoDomain}.auth.us-east-1.amazoncognito.com/logout?${params}`;
}

/** Clear local tokens and redirect to Cognito logout */
export function logout(): void {
  clearTokens();
  window.location.href = getLogoutUrl();
}

/**
 * Validate the OAuth callback and exchange its code for tokens.
 *
 * Why: a code is only redeemed if this tab started the login (state) and only by the party
 * holding the verifier (PKCE) — 011 finding 4.
 * How: `completeLogin` checks state and releases the verifier before any network call; the
 * token request carries `code_verifier`; tokens are stored in localStorage as before.
 *
 * @param params - The callback URL's query parameters.
 * @throws Error when state validation fails or the token exchange is refused.
 */
export async function handleCallback(params: URLSearchParams): Promise<void> {
  // Throws before any network call on a missing or mismatched state.
  const { code, verifier } = completeLogin(window.sessionStorage, params);
  const response = await fetch(
    `https://${config.cognitoDomain}.auth.us-east-1.amazoncognito.com/oauth2/token`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: config.cognitoClientId,
        code,
        redirect_uri: redirectUri(),
        code_verifier: verifier,
      }),
    },
  );

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`Token exchange failed: ${response.status} - ${errorBody}`);
  }

  const data = await response.json();
  const tokens: TokenSet = {
    idToken: data.id_token,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + data.expires_in * 1000,
  };
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
}

/** Get current ID token for API calls */
export function getIdToken(): string | null {
  const stored = localStorage.getItem(TOKEN_KEY);
  if (!stored) return null;

  const tokens: TokenSet = JSON.parse(stored);
  if (Date.now() > tokens.expiresAt) {
    clearTokens();
    return null;
  }
  return tokens.idToken;
}

/** Check if user is authenticated */
export function isAuthenticated(): boolean {
  return getIdToken() !== null;
}

/** Decode JWT payload to extract user info */
export function getUserInfo(): UserInfo | null {
  const token = getIdToken();
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      userId: payload.sub ?? 'unknown',
      email: payload.email ?? 'unknown',
      // Reviewer UI follows the admin-assigned group, never custom:role (011, D1).
      role: isReviewerFromIdTokenPayload(payload) ? 'reviewer' : 'user',
    };
  } catch {
    return null;
  }
}

function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
}
